import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { IoClose, IoCheckmarkCircle, IoLocation } from "react-icons/io5";
import UserAvatar from "./UserAvatar";
import { verifyCheckIn } from "../api/marketplaceAPI";
import { useSnackbar } from "../contexts/SnackbarContext";

/**
 * AirPlay-inspired Check-In Prompt Modal
 * Shows when two users with an accepted linkup request are near each other and the venue
 */
const CheckInPromptModal = ({
  open,
  onClose,
  // New props from socket context
  linkupRequestId,
  linkupId,
  linkupTitle,
  linkupActivity,
  linkupDate,
  partnerName,
  partnerAvatar,
  partnerId,
  venueName,
  venueAddress,
  distanceToVenue,
  distanceToPartner,
  userId,
  userLat,
  userLng,
  onCheckInSuccess,
  // Legacy props (for backwards compatibility)
  onCheckIn: legacyOnCheckIn,
  otherUser: legacyOtherUser,
  venue: legacyVenue,
  distanceToUser: legacyDistanceToUser,
  isLoading: externalIsLoading = false,
  alreadyCheckedIn = false,
}) => {
  const { addSnackbar } = useSnackbar();
  const [showSuccess, setShowSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);

  // Get fresh location when modal opens
  useEffect(() => {
    if (open && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          console.warn("Failed to get location for check-in:", error);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 },
      );
    }
  }, [open]);

  // Use fresh geolocation if available, otherwise fall back to props
  const effectiveUserLat = currentLocation?.lat ?? userLat;
  const effectiveUserLng = currentLocation?.lng ?? userLng;

  // Build otherUser object from new props or use legacy
  const otherUser = legacyOtherUser || {
    name: partnerName,
    avatar: partnerAvatar,
  };

  // Build venue object from new props or use legacy
  const venue =
    legacyVenue ||
    (venueName ? { name: venueName, address: venueAddress } : null);

  // Use appropriate distance values
  const displayDistanceToUser = legacyDistanceToUser ?? distanceToPartner;
  const displayDistanceToVenue = distanceToVenue;

  // Haptic feedback on open
  useEffect(() => {
    if (open && navigator.vibrate) {
      navigator.vibrate([50, 30, 50]);
    }
  }, [open]);

  const handleCheckIn = useCallback(async () => {
    // Use legacy handler if provided
    if (legacyOnCheckIn) {
      try {
        await legacyOnCheckIn();
        setShowSuccess(true);
        if (navigator.vibrate) {
          navigator.vibrate([100, 50, 100, 50, 200]);
        }
        setTimeout(() => {
          setShowSuccess(false);
          onClose();
        }, 2000);
      } catch (err) {
        console.error("Check-in failed:", err);
      }
      return;
    }

    // New check-in flow using API
    console.log("[CheckIn] Params:", {
      linkupId,
      userId,
      effectiveUserLat,
      effectiveUserLng,
      currentLocation,
      propsLat: userLat,
      propsLng: userLng,
    });

    if (
      !linkupId ||
      !userId ||
      effectiveUserLat === undefined ||
      effectiveUserLng === undefined
    ) {
      console.error("Missing required check-in params:", {
        linkupId,
        userId,
        effectiveUserLat,
        effectiveUserLng,
      });
      return;
    }

    setIsLoading(true);
    try {
      const result = await verifyCheckIn({
        linkup_id: linkupId,
        linkup_request_id: linkupRequestId,
        user_id: userId,
        user_lat: effectiveUserLat,
        user_lng: effectiveUserLng,
      });

      if (result.success) {
        setShowSuccess(true);
        addSnackbar("You're checked in! ✓", { timeout: 4000 });
        // Haptic success
        if (navigator.vibrate) {
          navigator.vibrate([100, 50, 100, 50, 200]);
        }
        // Notify parent of successful check-in
        if (onCheckInSuccess) {
          onCheckInSuccess(linkupRequestId);
        }
        // Auto-close after success
        setTimeout(() => {
          setShowSuccess(false);
          onClose();
        }, 2000);
      } else {
        // Handle check-in failure
        const rawError = result.error || result.message || "";
        const isTechnicalError =
          /column|table|sql|query|syntax|relation|constraint|undefined|null/i.test(
            rawError,
          );
        addSnackbar(
          isTechnicalError
            ? "Check-in failed. Please try again."
            : rawError || "Check-in failed. Try again.",
        );
      }
    } catch (err) {
      console.error("Check-in failed:", err);
      const rawApiError =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        "";
      const isTechnicalError =
        /column|table|sql|query|syntax|relation|constraint|undefined|null/i.test(
          rawApiError,
        );
      addSnackbar(
        isTechnicalError
          ? "Check-in failed. Please try again."
          : rawApiError || "Check-in failed. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    legacyOnCheckIn,
    onClose,
    linkupId,
    linkupRequestId,
    userId,
    effectiveUserLat,
    effectiveUserLng,
    currentLocation,
    userLat,
    userLng,
    onCheckInSuccess,
    addSnackbar,
  ]);

  const loading = isLoading || externalIsLoading;

  if (!open) return null;

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[9998] bg-black/60 backdrop-blur-md"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 50 }}
            transition={{
              type: "spring",
              damping: 25,
              stiffness: 300,
            }}
            className="fixed inset-x-4 bottom-8 z-[9999] mx-auto max-w-sm"
          >
            <div className="relative overflow-hidden rounded-[28px] bg-white shadow-2xl">
              {/* Top accent bar */}
              <div className="h-1 bg-gradient-to-r from-cyan-400 via-teal-400 to-emerald-400" />

              {/* Close button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors z-10"
              >
                <IoClose size={18} className="text-gray-600" />
              </button>

              <div className="px-6 pt-6 pb-8">
                {/* Success State */}
                {showSuccess ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex flex-col items-center py-8"
                  >
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{
                        type: "spring",
                        damping: 10,
                        stiffness: 200,
                        delay: 0.1,
                      }}
                    >
                      <IoCheckmarkCircle
                        size={72}
                        className="text-emerald-500"
                      />
                    </motion.div>
                    <h3 className="mt-4 text-xl font-bold text-gray-900">
                      Checked In!
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Enjoy your linkup with {otherUser?.name?.split(" ")[0]}
                    </p>
                  </motion.div>
                ) : alreadyCheckedIn ? (
                  /* Already Checked In State */
                  <div className="flex flex-col items-center py-6">
                    <div className="relative mb-4">
                      <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center">
                        <IoCheckmarkCircle
                          size={48}
                          className="text-emerald-500"
                        />
                      </div>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">
                      You're Checked In
                    </h3>
                    <p className="mt-1 text-sm text-gray-500 text-center">
                      Waiting for {otherUser?.name?.split(" ")[0]} to check in
                    </p>
                  </div>
                ) : (
                  /* Check-In Prompt State */
                  <>
                    {/* Header */}
                    <div className="text-center mb-5">
                      <p className="text-[10px] font-black text-cyan-600 uppercase tracking-[0.2em] mb-1">
                        Nearby
                      </p>
                      <h2 className="text-xl font-bold text-gray-900">
                        Ready to Link Up?
                      </h2>
                    </div>

                    {/* Avatar with AirPlay-style pulsing rings */}
                    <div className="flex justify-center mb-5">
                      <div className="relative w-28 h-28 flex items-center justify-center">
                        {/* Outer pulsing ring 1 */}
                        <motion.div
                          className="absolute inset-0 rounded-full"
                          style={{
                            background:
                              "linear-gradient(135deg, rgba(0,151,167,0.15) 0%, rgba(0,121,107,0.1) 100%)",
                          }}
                          animate={{
                            scale: [1, 1.3],
                            opacity: [0.6, 0],
                          }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: "easeOut",
                          }}
                        />

                        {/* Outer pulsing ring 2 (delayed) */}
                        <motion.div
                          className="absolute inset-0 rounded-full"
                          style={{
                            background:
                              "linear-gradient(135deg, rgba(0,151,167,0.12) 0%, rgba(0,121,107,0.08) 100%)",
                          }}
                          animate={{
                            scale: [1, 1.25],
                            opacity: [0.4, 0],
                          }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: "easeOut",
                            delay: 0.6,
                          }}
                        />

                        {/* Static gradient ring */}
                        <div
                          className="absolute rounded-full"
                          style={{
                            inset: "8px",
                            background:
                              "linear-gradient(135deg, #0097A7 0%, #00796B 100%)",
                            padding: "3px",
                          }}
                        >
                          <div className="w-full h-full rounded-full bg-white" />
                        </div>

                        {/* Avatar container */}
                        <div
                          className="absolute rounded-full overflow-hidden shadow-xl"
                          style={{
                            inset: "14px",
                          }}
                        >
                          <UserAvatar
                            userData={otherUser}
                            width="100%"
                            height="100%"
                          />
                        </div>

                        {/* Online indicator */}
                        <div className="absolute bottom-2 right-2 w-5 h-5 bg-emerald-500 rounded-full border-[3px] border-white shadow-md z-20" />
                      </div>
                    </div>

                    {/* Distance badge - separate from avatar */}
                    <div className="flex justify-center mb-4">
                      <div className="px-3 py-1.5 bg-gray-900 rounded-full flex items-center gap-1.5">
                        <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-pulse" />
                        <span className="text-[11px] font-bold text-white">
                          {displayDistanceToUser}m away
                        </span>
                      </div>
                    </div>

                    {/* User name */}
                    <p className="text-center text-lg font-semibold text-gray-900 mb-2">
                      {otherUser?.name}
                    </p>

                    {/* Linkup info */}
                    {(linkupTitle || linkupActivity) && (
                      <div className="text-center mb-4">
                        <p className="text-sm text-gray-600">
                          {linkupTitle || linkupActivity}
                        </p>
                        {linkupDate && (
                          <p className="text-xs text-gray-400 mt-0.5">
                            {new Date(linkupDate).toLocaleDateString(
                              undefined,
                              {
                                weekday: "short",
                                month: "short",
                                day: "numeric",
                                hour: "numeric",
                                minute: "2-digit",
                              },
                            )}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Venue info */}
                    {venue && (
                      <div className="flex items-center justify-center gap-2 mb-6 px-4 py-3 bg-gray-50 rounded-xl">
                        <IoLocation size={18} className="text-red-500" />
                        <div className="text-center">
                          <p className="text-sm font-semibold text-gray-900">
                            {venue.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {displayDistanceToVenue}m from venue
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Check-In Button */}
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleCheckIn}
                      disabled={loading}
                      className="w-full py-4 rounded-2xl font-bold text-white text-base transition-all disabled:opacity-50"
                      style={{
                        background:
                          "linear-gradient(135deg, #0097A7 0%, #00796B 100%)",
                        boxShadow: "0 4px 14px rgba(0, 151, 167, 0.4)",
                      }}
                    >
                      {loading ? (
                        <span className="flex items-center justify-center gap-2">
                          <motion.span
                            animate={{ rotate: 360 }}
                            transition={{
                              duration: 1,
                              repeat: Infinity,
                              ease: "linear",
                            }}
                            className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                          />
                          Checking In...
                        </span>
                      ) : (
                        "Check In Together"
                      )}
                    </motion.button>

                    {/* Skip option */}
                    <button
                      onClick={onClose}
                      className="w-full mt-3 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      Not now
                    </button>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CheckInPromptModal;
